import {
    trackEvent,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
} from 'dibs-tracking';

const { EVENT_WISHLIST } = eventNameConstants;
const { INTERACTION_TYPE_ITEM_FAVORITING, INTERACTION_TYPE_ITEM_WATCH } = interactionTypeConstants;
const {
    STEP_INTERACTION_ITEM_ADDED,
    STEP_INTERACTION_ITEM_REMOVED,
    STEP_INTERACTION_ITEM_ADDED_TO_FOLDER,
} = stepInteractionConstants;

const AUCTION_PDP = 'AUCTION_PDP';
const SIDEBAR = 'SIDEBAR';
const SELLER_GRID = 'SELLER_GRID';
const SIMILAR_ITEM_GRID = 'SIMILAR_ITEM_GRID';
const SIMILAR_AUCTION_ITEM_GRID = 'SIMILAR_AUCTION_ITEM_GRID';
const SIMILAR_ITEM_TOP = 'SIMILAR_ITEM_TOP';
const PERSONALIZED_ITEM_LIST = 'PERSONALIZED_ITEM_LIST';
const COLLECTION_ITEM_LIST = 'COLLECTION_ITEM_LIST';
const SIMILAR_AUCTION_ITEM_TOP = 'SIMILAR_AUCTION_ITEM_TOP';
const VISUALLY_SIMILAR_ITEM_GRID = 'VISUALLY_SIMILAR_ITEM_GRID';
const VISUALLY_SIMILAR_ITEM_LIST = 'VISUALLY_SIMILAR_ITEM_LIST';
const VISUALLY_SIMILAR_AUCTION_ITEM_GRID = 'VISUALLY_SIMILAR_AUCTION_ITEM_GRID';
const SEARCH_BROWSE = 'SEARCH_BROWSE';
const ZOOM_MODAL = 'ZOOM_MODAL';
const MOBILE_NAV = 'MOBILE_NAV';
const MOBILE_AUCTION_NAV = 'MOBILE_AUCTION_NAV';
const VISUALLY_SIMILAR_AUCTION_ITEM = 'VISUALLY_SIMILAR_AUCTION_ITEM';
const AUCTION_PDP_WATCH = 'AUCTION_PDP_WATCH';
const AUTO_EMAIL = 'AUTO_EMAIL';
const SIMILAR_SOLD = 'SIMILAR_SOLD';

export const locations = {
    AUCTION_PDP,
    SIDEBAR,
    SELLER_GRID,
    SIMILAR_ITEM_GRID,
    SIMILAR_ITEM_TOP,
    PERSONALIZED_ITEM_LIST,
    COLLECTION_ITEM_LIST,
    VISUALLY_SIMILAR_ITEM_GRID,
    VISUALLY_SIMILAR_ITEM_LIST,
    MOBILE_AUCTION_NAV,
    SEARCH_BROWSE,
    ZOOM_MODAL,
    MOBILE_NAV,
    VISUALLY_SIMILAR_AUCTION_ITEM,
    AUCTION_PDP_WATCH,
    AUTO_EMAIL,
    SIMILAR_SOLD,
};

// used to override locations passed from FavoritesProvider for auction items
export const auctionLocations = {
    [locations.SIDEBAR]: AUCTION_PDP,
    [locations.MOBILE_NAV]: MOBILE_AUCTION_NAV,
    [locations.SIMILAR_ITEM_TOP]: SIMILAR_AUCTION_ITEM_TOP,
    [locations.VISUALLY_SIMILAR_ITEM_GRID]: VISUALLY_SIMILAR_AUCTION_ITEM_GRID,
    [locations.SIMILAR_ITEM_GRID]: SIMILAR_AUCTION_ITEM_GRID,
};

export const authLabels = {
    AUCTION_PDP: 'save item - auction PDP',
    SIDEBAR: 'save item - product page',
    SELLER_GRID: 'save item - product page - more from dealer',
    SIMILAR_ITEM_GRID: 'save item - product page - you may also like',
    SIMILAR_ITEM_TOP: 'save item - product page - top you may also like',
    SEARCH_BROWSE: 'save item - search/browse',
    MOBILE_NAV: 'save item - product nav',
    AUCTION_PDP_WATCH: 'watch item - auction PDP',
};

const heartLabels = {
    SIDEBAR: 'PDP',
    SELLER_GRID: 'PDP - more from dealer',
    SIMILAR_ITEM_GRID: 'PDP - you may also like',
    SIMILAR_AUCTION_ITEM_GRID: 'auction pdp - you may also like',
    SIMILAR_ITEM_TOP: 'PDP - top you may also like',
    PERSONALIZED_ITEM_LIST: 'PDP - personalized items carousel',
    COLLECTION_ITEM_LIST: 'PDP - collection items carousel',
    SIMILAR_AUCTION_ITEM_TOP: 'auction pdp - top you may also like',
    MOBILE_NAV: 'PDP - mobile navigation',
    MOBILE_AUCTION_NAV: 'auction pdp - mobile navigation',
    VISUALLY_SIMILAR_ITEM_GRID: 'PDP - visually similar item',
    VISUALLY_SIMILAR_ITEM_LIST: 'PDP - visually similar items carousel',
    VISUALLY_SIMILAR_AUCTION_ITEM_GRID: 'auction pdp - visually similar item',
    SEARCH_BROWSE: 'search/browse',
    VISUALLY_SIMILAR_AUCTION_ITEM: 'visually similar auction item',
    AUCTION_PDP: 'auction pdp',
    AUCTION_PDP_WATCH: 'auction pdp watch',
    AUTO_EMAIL: 'PDP - auto email',
    SIMILAR_SOLD: 'similar sold item',
};

const folderLabels = {
    SEARCH_BROWSE: 'search/browse',
    SIMILAR_SOLD: 'similar sold item',
    PDP: 'pdp',
    AUCTION_PDP: 'auction pdp',
};

const interactionTypes = {
    [AUCTION_PDP_WATCH]: INTERACTION_TYPE_ITEM_WATCH,
};

function trackHeartEvent(location, action, itemId, step_interaction_name, isSponsoredItem = null) {
    trackEvent(
        {
            action,
            category: 'item favoriting',
            label: heartLabels[location],
            isInteractiveEvent: true,
            additional: {
                ...(isSponsoredItem !== null ? { is_sponsored_item: isSponsoredItem } : {}),
                ...(itemId ? { itemId } : {}),
            },
            eventName: EVENT_WISHLIST,
            interaction_type: interactionTypes[location] || INTERACTION_TYPE_ITEM_FAVORITING,
            step_interaction_name,
        },
        null
    );
}

export const trackHeartFavorited = (location, itemPk, isSponsoredItem) =>
    trackHeartEvent(location, 'item added', itemPk, STEP_INTERACTION_ITEM_ADDED, isSponsoredItem);

export const trackHeartUnfavorited = (location, itemPk, isSponsoredItem) =>
    trackHeartEvent(
        location,
        'item removed',
        itemPk,
        STEP_INTERACTION_ITEM_REMOVED,
        isSponsoredItem
    );

function trackFolderEvent(action, location, noteIncluded, itemPk) {
    const label = folderLabels[location] || folderLabels.PDP;
    trackEvent(
        {
            action,
            label,
            category: 'item favoriting',
            value: noteIncluded ? 1 : 0,
            isInteractiveEvent: true,
            additional: itemPk ? { itemPk } : {},
        },
        null
    );
}

const trackFolderClick = location => trackFolderEvent('item favoriting folder clicked', location);
const trackItemRemoved = location => trackFolderEvent('item removed from folder', location);
const trackItemAdded = (location, noteIncluded, itemPk) =>
    trackEvent(
        {
            action: 'item added into folder',
            label: heartLabels[location],
            category: 'item favoriting',
            value: noteIncluded ? 1 : 0,
            isInteractiveEvent: true,
            additional: itemPk ? { itemPk } : {},
            eventName: EVENT_WISHLIST,
            interaction_type: INTERACTION_TYPE_ITEM_FAVORITING,
            step_interaction_name: STEP_INTERACTION_ITEM_ADDED_TO_FOLDER,
        },
        null
    );
const trackFolderCreated = (location, noteIncluded) =>
    trackFolderEvent('item favoriting folder created', location, noteIncluded);

export const folderTrackingHooks = {
    trackFolderClick,
    trackItemRemoved,
    trackItemAdded,
    trackFolderCreated,
};
